import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { useAuth } from './auth-context.js';
import { AuthRahmen } from './auth-rahmen.js';
import type { Einrichtung, EinrichtungsDienst } from './enroll-service.js';
import { AnmeldeFehler } from './sign-in-service.js';

function meldungVon(fehler: unknown): string {
  return fehler instanceof AnmeldeFehler
    ? fehler.text
    : 'Die Einrichtung ist gescheitert. Bitte versuchen Sie es erneut.';
}

export function EnrollPage({ dienst }: { dienst: EinrichtungsDienst }) {
  const { laedt, nutzer, meldeAb } = useAuth();
  const navigiere = useNavigate();

  const [einrichtung, setEinrichtung] = useState<Einrichtung | null>(null);
  const [code, setCode] = useState('');
  const [meldung, setMeldung] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  /**
   * Das KONTO hat schon einen Faktor, nur diese SITZUNG traegt den Anspruch nicht -- eine
   * wiederhergestellte Anmeldung von vor der Einrichtung etwa, oder ein zweites Geraet. Der
   * Guard schickt jede anspruchslose Sitzung hierher; ohne diese Weiche schriebe der Nutzer
   * dann einen ZWEITEN Faktor ein, statt sich mit dem vorhandenen auszuweisen.
   *
   * Abgemeldet wird bewusst, nicht bloss umgeleitet: Die Aufforderung zum zweiten Faktor
   * entsteht erst bei `signInWithEmailAndPassword` (`auth/multi-factor-auth-required`), und
   * der Resolver lebt nur im Speicher der Anmeldeseite. Eine blosse Umleitung liefe im
   * Kreis -- der Guard schickt dieselbe Sitzung sofort wieder hierher.
   */
  const mussNeuAnmelden =
    !laedt && nutzer !== null && !nutzer.mitZweitemFaktor && nutzer.hatFaktorEingeschrieben;

  const brauchtEinrichtung =
    !laedt && nutzer !== null && !nutzer.mitZweitemFaktor && !nutzer.hatFaktorEingeschrieben;

  /**
   * Die Weiterleitung wartet auf die Abmeldung, sie startet sie nicht nur nebenbei: Solange
   * die Sitzung noch steht, sieht die Anmeldeseite einen angemeldeten Nutzer und leitet auf
   * "/" um, von wo der Guard sofort wieder hierher schickt. Scheitert `signOut` -- offline
   * etwa --, liefe diese Schleife endlos. Deshalb bis dahin ein Ladezustand und im
   * Fehlerfall eine Meldung mit einem zweiten Versuch.
   */
  const [abmeldung, setAbmeldung] = useState<'ruht' | 'laeuft' | 'gescheitert'>('ruht');
  const abgemeldet = useRef(false);

  useEffect(() => {
    if (!mussNeuAnmelden || abgemeldet.current) return;
    abgemeldet.current = true;
    setAbmeldung('laeuft');
    meldeAb().catch(() => setAbmeldung('gescheitert'));
  }, [mussNeuAnmelden, meldeAb]);

  function nochmalAbmelden() {
    setAbmeldung('laeuft');
    meldeAb().catch(() => setAbmeldung('gescheitert'));
  }

  useEffect(() => {
    if (!brauchtEinrichtung) return;
    let abgebaut = false;
    dienst
      .starte()
      .then((ergebnis) => {
        if (!abgebaut) setEinrichtung(ergebnis);
      })
      .catch((fehler: unknown) => {
        if (!abgebaut) setMeldung(meldungVon(fehler));
      });
    return () => {
      abgebaut = true;
    };
  }, [brauchtEinrichtung, dienst]);

  // In der Marken-Karte wie der Rest der Seite (Story "UI und Einladungsmails durchgaengig
  // gestalten").
  if (laedt) {
    return (
      <AuthRahmen>
        <p role="status">Anmeldung wird geprüft …</p>
      </AuthRahmen>
    );
  }
  if (!nutzer) return <Navigate to="/anmelden" replace />;
  if (nutzer.mitZweitemFaktor) return <Navigate to="/" replace />;

  if (mussNeuAnmelden) {
    if (abmeldung === 'gescheitert') {
      return (
        <AuthRahmen>
          <h1>Bitte melden Sie sich neu an</h1>
          <div className="eds-alert eds-alert--error" role="alert">
            <p>
              Sie haben bereits einen zweiten Faktor eingerichtet. Für den Zugang müssen Sie sich
              damit neu anmelden, die Abmeldung ist aber gescheitert. Bitte prüfen Sie Ihre
              Verbindung.
            </p>
          </div>
          <button
            className="eds-button eds-button--primary"
            type="button"
            onClick={nochmalAbmelden}
          >
            Erneut versuchen
          </button>
        </AuthRahmen>
      );
    }
    return (
      <AuthRahmen>
        <p role="status">Sie haben bereits einen zweiten Faktor. Sie werden abgemeldet …</p>
      </AuthRahmen>
    );
  }

  function abschicken(ereignis: FormEvent) {
    ereignis.preventDefault();
    setMeldung(null);
    setSendet(true);
    void dienst
      .bestaetige(code)
      .then(async (ergebnis) => {
        if (ergebnis === 'anspruch-da') {
          navigiere('/', { replace: true });
          return;
        }
        await meldeAb();
        navigiere('/anmelden', { replace: true });
      })
      .catch((fehler: unknown) => setMeldung(meldungVon(fehler)))
      .finally(() => setSendet(false));
  }

  return (
    <AuthRahmen>
      <h1>Zweiten Faktor einrichten</h1>
      <p>
        Für den Zugang brauchen Sie eine Authenticator-App auf Ihrem Telefon, also eine App, die
        alle 30 Sekunden einen neuen sechsstelligen Code anzeigt. Scannen Sie den Code unten mit
        dieser App und geben Sie dann den angezeigten Code ein.
      </p>

      {meldung && (
        <div className="eds-alert eds-alert--error" role="alert">
          <p>{meldung}</p>
        </div>
      )}

      {einrichtung && (
        <>
          {/* Der QR-Code kommt aus `qrcode` im eigenen Bundle, nicht von einem Fremdhost. */}
          <div
            aria-label="QR-Code"
            role="img"
            dangerouslySetInnerHTML={{ __html: einrichtung.qrCodeSvg }}
          />
          <p>
            Wenn Sie nicht scannen können, tragen Sie diesen Schlüssel von Hand in die App ein:{' '}
            <code>{einrichtung.geheimnis}</code>
          </p>

          <form onSubmit={abschicken}>
            <div className="eds-field">
              <label className="eds-field__label" htmlFor="code">
                Sechsstelliger Code
              </label>
              <input
                className="eds-field__input"
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
            <button className="eds-button eds-button--primary" type="submit" disabled={sendet}>
              Einrichtung abschließen
            </button>
          </form>
        </>
      )}
    </AuthRahmen>
  );
}
